import {
  FastField,
  useField,
  FastFieldProps,
  FieldInputProps,
  FormikProps,
  FormikHelpers,
  GenericFieldHTMLAttributes,
  FormikHandlers,
  getIn,
  Form,
  Formik,
  useFormikContext,
} from 'formik'
import { z } from 'zod'
import * as yup from 'yup'
import { isIOS } from 'react-device-detect'

type FieldHtmlAttributesWithFormik<V> = GenericFieldHTMLAttributes & {
  formik: FormikProps<V>
  readOnly?: boolean
  name: string | string[]
}

const shouldUpdate = <V>(next: FieldHtmlAttributesWithFormik<V>, current: FieldHtmlAttributesWithFormik<V>) =>
  next.name !== current.name ||
  next.required !== current.required ||
  next.disabled !== current.disabled ||
  next.readOnly !== current.readOnly ||
  next.formik.isSubmitting !== current.formik.isSubmitting ||
  Object.keys(next).length !== Object.keys(current).length ||
  getIn(next.formik.values, current.name) !== getIn(current.formik.values, current.name) ||
  getIn(next.formik.errors, current.name) !== getIn(current.formik.errors, current.name) ||
  getIn(next.formik.touched, current.name) !== getIn(current.formik.touched, current.name)

// reason: UID should be in capital
// eslint-disable-next-line @typescript-eslint/naming-convention
const generateUIDX = (): string => (Date.now().toString(36) + Math.random().toString(36).substring(2, 5)).toUpperCase()

// This weird looking calculation is because Enters (CRLF) are counted differently in iOS with regard to the maxLength property
// => They are 2 characters instead of 1.
const CRLFRegEx = /\r(?!\n)|\n(?!\r)/g
const CRLFReplacer = isIOS ? 'rn' : 'r'

const calculateLength = (value: string): number => value.replace(CRLFRegEx, CRLFReplacer).length

export {
  calculateLength,
  Form,
  Formik,
  FastField,
  generateUIDX,
  shouldUpdate,
  type FormikHandlers,
  type FormikHelpers,
  type FastFieldProps,
  type FormikProps,
  type FieldInputProps,
  useField,
  useFormikContext,
  yup,
  z,
}
